import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { SOCIAL_REPLY_MAX_LENGTH, } from '@wit-support/admin-contracts';
import { ConfirmDialog, Dialog, DialogContent, DialogDescription, DialogTitle, InlineNotice, toast, } from '@wit-support/ui';
import { Actions, CancelButton, SubmitButton, ZodField, ZodForm } from '@wit-support/ui-forms';
import { approveAction, dismissComment, rejectAction, requestAction } from './api.js';
import { SOCIAL_ACTION_KIND_LABEL_RU, SOCIAL_BLOCKED_TEXT_RU, blockPauseActive, blockedText, describePauseReason, } from './outcome-text.js';
import { commentAuthorLabel, netText, siblingNetworkOf } from './network-text.js';
import { describeSuggestion, humanSuggestionPending } from './policy-text.js';
import { doneOnlyDry } from './shadow-text.js';
import { SOCIAL_KEY, describeSocialError, submitting, upstreamCodeOf } from './shared.js';
/** Порядок действий в меню и на карточке. */
export const COMMENT_ACTION_KINDS = [
    'like',
    'reply',
    'hide',
    'delete',
];
/** Кнопка «Отметить просмотренным» и подпись состояния `ignored`. */
export const DISMISS_LABEL = 'Отметить просмотренным';
/** Без действий по отметке человека; по решению алгоритма — `ALGO_NO_ACTION_LABEL` (shadow-text). */
export const DISMISSED_LABEL = 'Просмотрен — без действий';
export const DISMISSED_TOAST = 'Отмечено: просмотрен, без действий';
/**
 * «Отметить просмотренным» имеет смысл у комментария, который ждёт решения
 * (`new`), и у комментария в ручной очереди «Нужен человек», даже если по нему
 * уже есть действие (лайк поставлен, а вопрос ждёт ответа): движок E-POL
 * тогда только снимает его из очереди, состояние не меняет. У `ignored` —
 * отметка уже есть.
 */
export function canDismiss(comment) {
    return comment.state === 'new' || humanSuggestionPending(comment);
}
export const HIDDEN_REPLY_TEXT = 'Нельзя ответить на скрытый комментарий';
export const DELETE_CONFIRM_TEXT = 'Удаление необратимо: комментарий исчезнет из соцсети, вернуть его будет нельзя.';
/** «Что умеем», когда подключения для аккаунта × сети нет вовсе. */
export const NO_CAPABILITIES = {
    listComments: false,
    reply: false,
    like: false,
    hide: false,
    delete: false,
    why: {},
};
/** Действие поставлено и ждёт исполнителя: повторный запрос движок отвергнет 409. */
const QUEUED_STATUSES = ['approved', 'executing'];
export const QUEUED_TEXT = 'Уже в очереди — движок выполнит в ближайшие минуты';
/**
 * 409 `social_action_exists` — не ошибка человека: такое действие уже стоит в
 * очереди или выполнено. Показываем спокойным тостом, не красным.
 */
export const ACTION_EXISTS_TEXT = 'Такое действие уже есть — оно в очереди или выполнено, ставить его ещё раз не нужно.';
/**
 * «Отменить» поставленное действие: движок принимает отмену `approved`, пока
 * исполнитель его не взял, в ЛЮБОМ режиме и при блоке/стопе (RULINGS-SAFETY-2
 * M2: очередь, накопленную за паузу, должно быть можно разобрать до
 * «Возобновить»). Опоздали — 409 `social_action_not_pending`.
 */
export function canCancel(a) {
    return a.status === 'approved';
}
export const CANCEL_TOO_LATE_TEXT = 'Отменить уже нельзя: действие выполняется или выполнено. Обновите страницу.';
const PAIR_GATE_TEXT_RU = {
    blocked: `${SOCIAL_BLOCKED_TEXT_RU}. Возобновить может владелец`,
    global_stop: 'Включён «Стоп всех действий» — действия не отправляются',
    manual_stop: 'Действия аккаунта остановлены вручную — нужно «Возобновить» (владелец)',
    mode_off: 'Режим аккаунта «Выключено» — действия не отправляются',
};
/**
 * Почему кнопки действий над комментариями этого аккаунта × сети сейчас
 * серые (RULINGS-SAFETY-2); null — действия принимаются. Фраза движка
 * (`safety.statusText`) важнее своей, если состояние то же. Старый движок
 * (`safety: null`) — по общему стопу и режиму из настроек.
 *
 * Пауза после ограничения Instagram (`paused` с причиной `blocked_*`, первая
 * 368 — 48 ч) — тоже «заблокирован» (RULINGS-SAFETY-3 H1): движок такие
 * действия не принимает (409), а повтор действия-триггера ушёл бы первым
 * после паузы. Причина — фраза паузы движка.
 */
export function pairGateReason(status, account, network) {
    if (!status)
        return null;
    const cell = status.items.find((c) => c.account === account && c.network === network);
    const safety = cell?.safety ?? null;
    const hardBlocked = Boolean(safety?.blockedAt) || safety?.status === 'blocked';
    const blockPause = blockPauseActive(safety);
    const gate = hardBlocked || blockPause
        ? 'blocked'
        : status.globalStop?.stopped || safety?.status === 'global_stop'
            ? 'global_stop'
            : safety?.stoppedAt || safety?.status === 'manual_stop'
                ? 'manual_stop'
                : safety?.status === 'mode_off' || cell?.settings?.mode === 'off'
                    ? 'mode_off'
                    : null;
    if (gate === null)
        return null;
    if (!hardBlocked && blockPause && safety) {
        const pause = (safety.pauseText ??
            describePauseReason(safety.pauseReason, network, cell ? siblingNetworkOf(cell, status.items, safety.pauseReason) : null)).replace(/[.\s]+$/, '');
        return `${pause}. Не повторяйте действия, пока ограничение не снято`;
    }
    if (safety?.status === gate && safety.statusText)
        return safety.statusText;
    return gate === 'blocked'
        ? `${blockedText(network)}. Возобновить может владелец`
        : PAIR_GATE_TEXT_RU[gate];
}
/** Виды действий из журнала, которые сейчас в очереди (`approved`/`executing`). */
export function queuedKinds(journal) {
    return new Set(journal.filter((a) => QUEUED_STATUSES.includes(a.status)).map((a) => a.kind));
}
/**
 * Почему действие сейчас недоступно (для серой кнопки); null — доступно.
 * Порядок проверок — от того, что объясняет больше всего. `queued` — виды
 * действий, уже стоящих в очереди (журнал карточки и то, что поставили на
 * этом экране): в `comment.pending` движок кладёт только `proposed`, и без
 * этого вторая кнопка давала бы 409. `gate` — {@link pairGateReason}:
 * аккаунт заблокирован / остановлен / выключен — движок не примет (409) или
 * поставит в очередь, которая не исполняется.
 */
export function actionUnavailableReason(kind, comment, caps, queued = new Set(), gate = null) {
    if (comment.deleted)
        return 'Комментарий уже удалён';
    if (gate !== null)
        return gate;
    if (kind === 'reply' && comment.hidden === true)
        return HIDDEN_REPLY_TEXT;
    if (kind === 'hide' && comment.isOwn)
        return 'Это наш комментарий — его нельзя скрыть';
    if (kind === 'hide' && comment.hidden === true)
        return 'Комментарий уже скрыт';
    if (!caps[kind])
        return caps.why[kind] ?? 'Это действие недоступно при текущем подключении';
    if (comment.done.includes(kind))
        return doneOnlyDry(comment, kind) ? DRY_DONE_TEXT : 'Уже выполнено';
    if (comment.pending.some((a) => a.kind === kind))
        return 'Уже предложено — подтвердите или отклоните';
    if (queued.has(kind))
        return QUEUED_TEXT;
    return null;
}
/**
 * Вид уже отмечен выполненным в сухом режиме: в сеть не уходил, но движок
 * считает его выполненным и второй такой же не примет (409
 * `social_action_exists`).
 */
export const DRY_DONE_TEXT = 'Уже отмечено в сухом режиме — в сеть не уходило, но поставить ещё раз движок не даст';
/**
 * Подсказка под полем ответа: правила, по которым движок не отправит ответ
 * (SAFE-E, анти-спам Instagram) — лучше узнать до «Ответить».
 */
export const REPLY_RULES_HINT = 'Не меньше 3 слов, без ссылок и @-упоминаний, не только эмодзи. Одному человеку — не больше одного ответа под постом; один и тот же текст — не чаще раза в сутки. Иначе Instagram может счесть ответ спамом.';
/** Те же правила с именем сети комментария (у Instagram — {@link REPLY_RULES_HINT}). */
export function replyRulesHint(network) {
    return netText(REPLY_RULES_HINT, network);
}
/** 422 `social_action_unsafe`: движок не пропустил действие по правилам защиты. */
const UNSAFE_CODE = 'social_action_unsafe';
export const replySchema = z.object({
    text: z
        .string()
        .trim()
        .min(1, 'Напишите текст ответа')
        .max(SOCIAL_REPLY_MAX_LENGTH, `Не длиннее ${SOCIAL_REPLY_MAX_LENGTH} символов`)
        .meta({ label: 'Текст ответа', widget: 'textarea' }),
});
/** Черновик ответа для диалога «Подтвердить» — текст, который предложил алгоритм. */
export function approveDraft(a) {
    return a.text ?? '';
}
/**
 * Человек исправил текст предложенного ответа (пробелы по краям — не правка).
 * Исправленный текст уходит в одном `approve {text}`: движок проверяет его
 * правилами защиты (отказ — 422, действие не меняется) и пишет
 * `editedBy`/`editedAt`.
 */
export function replyEdited(a, text) {
    return a.text === null || text.trim() !== a.text.trim();
}
/** Тело подтверждения: текст — только если его исправили. */
export function approveBody(a, text) {
    return replyEdited(a, text) ? { text: text.trim() } : {};
}
/**
 * Действия над комментариями — одни для таблицы журнала и для карточки:
 * сердечко и «скрыть» уходят сразу, ответ — через диалог с текстом, удаление —
 * через `ConfirmDialog` (необратимо). Все действия ставятся в очередь
 * движка; исполнит их ближайший прогон.
 */
export function useCommentActions() {
    const qc = useQueryClient();
    const [target, setTarget] = useState(null);
    const [approveTarget, setApproveTarget] = useState(null);
    // Поставленное на этом экране: id действия (null — движок ответил «уже
    // есть», id не знаем). Живёт, пока экран открыт.
    const [local, setLocal] = useState([]);
    // Отказ правил защиты на ответ показываем в самом диалоге: текст правят там же.
    const [replyError, setReplyError] = useState(null);
    const remember = (entry) => setLocal((prev) => [...prev, entry]);
    const refresh = () => void qc.invalidateQueries({ queryKey: SOCIAL_KEY });
    const requestMut = useMutation({
        mutationFn: (v) => requestAction(v.commentId, v.text === undefined ? { kind: v.kind } : { kind: v.kind, text: v.text }),
        onSuccess: (a, v) => {
            remember({ commentId: v.commentId, kind: v.kind, actionId: a.id });
            const label = SOCIAL_ACTION_KIND_LABEL_RU[v.kind];
            // Движок уже знает, что действие подождёт (режим «Выключено», стоп,
            // лимит…) — говорим сразу, а не «в ближайшие минуты».
            if (a.waitText)
                toast.info(`«${label}» — в очереди, но пока ждёт: ${a.waitText}`);
            else
                toast.success(`«${label}» — в очереди: движок выполнит в ближайшие минуты`);
            setReplyError(null);
            setTarget(null);
            refresh();
        },
        onError: (err, v) => {
            if (v.kind === 'reply' && upstreamCodeOf(err) === UNSAFE_CODE) {
                setReplyError(describeSocialError(err, 'write', v.network));
                return;
            }
            if (upstreamCodeOf(err) === 'social_action_exists') {
                remember({ commentId: v.commentId, kind: v.kind, actionId: null });
                toast.info(ACTION_EXISTS_TEXT);
                setTarget(null);
            }
            else {
                toast.error(describeSocialError(err, 'write', v.network));
            }
            refresh();
        },
    });
    const approveMut = useMutation({
        mutationFn: (v) => approveAction(v.action.id, v.text === undefined ? {} : approveBody(v.action, v.text)),
        onSuccess: (r, v) => {
            const label = SOCIAL_ACTION_KIND_LABEL_RU[v.action.kind];
            toast.success(r.editedBy ? `«${label}» подтверждено с вашим текстом` : `«${label}» подтверждено`);
            setReplyError(null);
            setApproveTarget(null);
            refresh();
        },
        onError: (err, v) => {
            // Отказ правил защиты на текст ответа — в самом диалоге: правят там же;
            // движок действие не меняет, его можно подтвердить снова.
            if (v.action.kind === 'reply' && upstreamCodeOf(err) === UNSAFE_CODE) {
                setReplyError(describeSocialError(err, 'write', v.network));
                return;
            }
            toast.error(describeSocialError(err, 'write', v.network));
            setApproveTarget(null);
            refresh();
        },
    });
    const rejectMut = useMutation({
        mutationFn: (v) => rejectAction(v.action.id),
        onSuccess: (_r, v) => {
            toast.success(`«${SOCIAL_ACTION_KIND_LABEL_RU[v.action.kind]}» отклонено`);
            refresh();
        },
        onError: (err, v) => {
            toast.error(describeSocialError(err, 'write', v.network));
            refresh();
        },
    });
    const cancelMut = useMutation({
        mutationFn: (v) => rejectAction(v.action.id),
        onSuccess: (_r, v) => {
            toast.success(`«${SOCIAL_ACTION_KIND_LABEL_RU[v.action.kind]}» отменено — в сеть не уйдёт`);
            refresh();
        },
        onError: (err, v) => {
            if (upstreamCodeOf(err) === 'social_action_not_pending')
                toast.info(CANCEL_TOO_LATE_TEXT);
            else
                toast.error(describeSocialError(err, 'write', v.network));
            refresh();
        },
    });
    const dismissMut = useMutation({
        mutationFn: (c) => dismissComment(c.id),
        onSuccess: () => {
            toast.success(DISMISSED_TOAST);
            refresh();
        },
        onError: (err, c) => {
            toast.error(describeSocialError(err, 'write', c.network));
            refresh();
        },
    });
    const queuedFor = useCallback((comment, journal) => {
        const mine = local.filter((e) => e.commentId === comment.id);
        if (!journal)
            return new Set(mine.map((e) => e.kind));
        const out = queuedKinds(journal);
        const known = new Set(journal.map((a) => a.id));
        // Действие уже в журнале — решает его статус (вдруг оно упало и нужен
        // «Повторить»); «уже есть» без id журнал после перезапроса покажет сам.
        for (const e of mine)
            if (e.actionId !== null && !known.has(e.actionId))
                out.add(e.kind);
        return out;
    }, [local]);
    const start = (comment, kind, opts) => {
        setReplyError(null);
        if (kind === 'reply' || kind === 'delete') {
            setTarget({ comment, kind, text: opts?.text ?? undefined });
            return;
        }
        requestMut.mutate({ commentId: comment.id, kind, network: comment.network });
    };
    const author = (c) => commentAuthorLabel(c) ?? 'автору';
    const approveSuggestion = approveTarget
        ? describeSuggestion(approveTarget.action, approveTarget.comment?.category ?? null)
        : null;
    const dialogs = (_jsxs(_Fragment, { children: [target?.kind === 'reply' ? (_jsx(Dialog, { open: true, onOpenChange: (v) => (!v ? setTarget(null) : null), children: _jsxs(DialogContent, { size: "lg", children: [_jsxs(DialogTitle, { children: ["\u041E\u0442\u0432\u0435\u0442\u0438\u0442\u044C ", author(target.comment)] }), _jsxs(DialogDescription, { children: ["\u041E\u0442\u0432\u0435\u0442 \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F \u043F\u043E\u0434 \u043A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0435\u043C \u043F\u0443\u0431\u043B\u0438\u0447\u043D\u043E, \u043E\u0442 \u0438\u043C\u0435\u043D\u0438 \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430 ", target.comment.account, ". \u00AB", target.comment.text.slice(0, 140), target.comment.text.length > 140 ? '…' : '', "\u00BB"] }), _jsxs(ZodForm, { schema: replySchema, defaultValues: { text: target.text ?? '' }, onSubmit: (values) => submitting(requestMut.mutateAsync({
                                commentId: target.comment.id,
                                kind: 'reply',
                                text: String(values.text),
                                network: target.comment.network,
                            })), children: [_jsx(ZodField, { name: "text", hint: replyRulesHint(target.comment.network) }), replyError ? (_jsx(InlineNotice, { variant: "danger", bordered: true, role: "alert", children: replyError })) : null, _jsxs(Actions, { children: [_jsx(CancelButton, { onClick: () => setTarget(null) }), _jsx(SubmitButton, { pendingLabel: "\u041E\u0442\u043F\u0440\u0430\u0432\u043B\u044F\u0435\u043C\u2026", children: "\u041E\u0442\u0432\u0435\u0442\u0438\u0442\u044C" })] })] }, target.comment.id)] }) })) : null, approveTarget ? (_jsx(Dialog, { open: true, onOpenChange: (v) => (!v ? setApproveTarget(null) : null), children: _jsxs(DialogContent, { size: "lg", children: [_jsxs(DialogTitle, { children: ["\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C \u043E\u0442\u0432\u0435\u0442", approveTarget.comment ? ` ${author(approveTarget.comment)}` : ''] }), _jsxs(DialogDescription, { children: ["\u041E\u0442\u0432\u0435\u0442 \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F \u043F\u043E\u0434 \u043A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0435\u043C \u043F\u0443\u0431\u043B\u0438\u0447\u043D\u043E", approveTarget.comment ? `, от имени аккаунта ${approveTarget.comment.account}` : '', ". \u0422\u0435\u043A\u0441\u0442 \u043C\u043E\u0436\u043D\u043E \u0438\u0441\u043F\u0440\u0430\u0432\u0438\u0442\u044C \u043F\u0435\u0440\u0435\u0434 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u0435\u043C \u2014 \u0432 \u0436\u0443\u0440\u043D\u0430\u043B\u0435 \u0431\u0443\u0434\u0435\u0442 \u043E\u0442\u043C\u0435\u0447\u0435\u043D\u043E, \u043A\u0442\u043E \u0438\u0441\u043F\u0440\u0430\u0432\u0438\u043B.", approveTarget.comment ? (_jsxs(_Fragment, { children: [' ', "\u00AB", approveTarget.comment.text.slice(0, 140), approveTarget.comment.text.length > 140 ? '…' : '', "\u00BB"] })) : null] }), approveSuggestion?.why ? (_jsxs(InlineNotice, { variant: "muted", as: "p", children: ["\u0410\u043B\u0433\u043E\u0440\u0438\u0442\u043C \u043F\u0440\u0435\u0434\u043B\u0430\u0433\u0430\u0435\u0442 ", approveSuggestion.what, ": ", approveSuggestion.why, "."] })) : null, _jsxs(ZodForm, { schema: replySchema, defaultValues: { text: approveDraft(approveTarget.action) }, onSubmit: (values) => submitting(approveMut.mutateAsync({
                                action: approveTarget.action,
                                text: String(values.text),
                                network: approveTarget.comment?.network,
                            })), children: [_jsx(ZodField, { name: "text", hint: replyRulesHint(approveTarget.comment?.network) }), replyError ? (_jsx(InlineNotice, { variant: "danger", bordered: true, role: "alert", children: replyError })) : null, _jsxs(Actions, { children: [_jsx(CancelButton, { onClick: () => setApproveTarget(null) }), _jsx(SubmitButton, { pendingLabel: "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0430\u0435\u043C\u2026", children: "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C" })] })] }, approveTarget.action.id)] }) })) : null, _jsx(ConfirmDialog, { open: target?.kind === 'delete', onOpenChange: (v) => (!v ? setTarget(null) : null), title: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u043A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0439?", description: DELETE_CONFIRM_TEXT, cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C", actionStyle: "danger", onConfirm: () => {
                    if (target?.kind === 'delete') {
                        requestMut.mutate({
                            commentId: target.comment.id,
                            kind: 'delete',
                            network: target.comment.network,
                        });
                    }
                    setTarget(null);
                } })] }));
    return {
        start,
        approve: (a, comment) => {
            setReplyError(null);
            if (a.kind === 'reply') {
                setApproveTarget({ action: a, comment: comment ?? null });
                return;
            }
            approveMut.mutate({ action: a, network: comment?.network });
        },
        reject: (a, comment) => rejectMut.mutate({ action: a, network: comment?.network }),
        cancel: (a, comment) => cancelMut.mutate({ action: a, network: comment?.network }),
        dismiss: (c) => dismissMut.mutate(c),
        sendReply: (c, text) => {
            setReplyError(null);
            return submitting(requestMut.mutateAsync({ commentId: c.id, kind: 'reply', text, network: c.network }));
        },
        replyError,
        queuedFor,
        busy: requestMut.isPending ||
            approveMut.isPending ||
            rejectMut.isPending ||
            cancelMut.isPending ||
            dismissMut.isPending,
        dialogs,
    };
}
//# sourceMappingURL=comment-actions.js.map